/**
 * CardLine Games - Core Theme JavaScript
 * Handles: Cart AJAX, Cart Drawer, Predictive Search, Notifications
 */

(function () {
  'use strict';

  /* Safe JSON parser: returns null instead of throwing on empty/invalid body */
  function safeJson(res) {
    return res.text().then(function (text) {
      if (!text) return null;
      try { return JSON.parse(text); } catch (e) { return null; }
    });
  }

  /* ==========================================
     Cart API Helpers
     ========================================== */
  var CartAPI = {
    get: function () {
      return fetch(window.theme.routes.cart + '.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }).then(safeJson);
    },

    add: function (variantId, quantity) {
      return fetch(window.theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
      }).then(function (res) {
        if (!res.ok) {
          return safeJson(res).then(function (err) {
            err = err || {};
            throw new Error(err.description || err.message || 'Add to cart failed');
          });
        }
        return safeJson(res);
      });
    },

    change: function (line, quantity) {
      return fetch(window.theme.routes.cartChange, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ line: line, quantity: quantity })
      }).then(function (res) {
        if (!res.ok) {
          return safeJson(res).then(function (err) {
            err = err || {};
            throw new Error(err.description || err.message || 'Cart update failed');
          });
        }
        return safeJson(res);
      });
    },

    update: function (updates) {
      return fetch(window.theme.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(safeJson);
    }
  };

  /* ==========================================
     Toast Notifications
     ========================================== */
  var Toast = {
    el: null,

    init: function () {
      this.el = document.createElement('div');
      this.el.className = 'toast';
      this.el.innerHTML = '<span class="toast__message"></span><button type="button" class="toast__close" aria-label="Close"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></button>';
      document.body.appendChild(this.el);

      this.el.querySelector('.toast__close').addEventListener('click', function () {
        Toast.hide();
      });
    },

    show: function (message, type) {
      if (!this.el) this.init();
      this.el.querySelector('.toast__message').textContent = message;
      this.el.className = 'toast toast--' + (type || 'success');

      requestAnimationFrame(function () {
        Toast.el.classList.add('is-visible');
      });

      clearTimeout(this._timeout);
      this._timeout = setTimeout(function () {
        Toast.hide();
      }, 3500);
    },

    hide: function () {
      if (this.el) {
        this.el.classList.remove('is-visible');
      }
    }
  };

  /* ==========================================
     Cart Drawer
     ========================================== */
  var CartDrawer = {
    drawer: null,

    init: function () {
      this.drawer = document.querySelector('[data-cart-drawer]');
      if (!this.drawer) return;

      var self = this;

      // Close buttons
      this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.close(); });
      });

      // Overlay click
      var overlay = this.drawer.querySelector('[data-cart-drawer-overlay]');
      if (overlay) {
        overlay.addEventListener('click', function () { self.close(); });
      }

      // Cart toggle buttons in header
      document.querySelectorAll('.header__cart-toggle').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (window.theme.cartType === 'drawer') {
            self.open();
          } else {
            window.location.href = window.theme.routes.cart;
          }
        });
      });

      // Quantity and remove buttons
      this.bindItemEvents();

      // Escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.drawer.classList.contains('is-open')) {
          self.close();
        }
      });
    },

    open: function () {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
    },

    bindItemEvents: function () {
      if (!this.drawer) return;
      var self = this;

      // Quantity minus
      this.drawer.querySelectorAll('[data-qty-minus]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = this.closest('[data-cart-item]');
          var input = item.querySelector('[data-qty-input]');
          var val = parseInt(input.value) || 1;
          if (val > 0) {
            input.value = val - 1;
            self.updateItemQuantity(item, val - 1);
          }
        });
      });

      // Quantity plus
      this.drawer.querySelectorAll('[data-qty-plus]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = this.closest('[data-cart-item]');
          var input = item.querySelector('[data-qty-input]');
          var val = parseInt(input.value) || 1;
          input.value = val + 1;
          self.updateItemQuantity(item, val + 1);
        });
      });

      // Remove
      this.drawer.querySelectorAll('[data-cart-remove]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = this.closest('[data-cart-item]');
          self.updateItemQuantity(item, 0);
        });
      });
    },

    updateItemQuantity: function (itemEl, quantity) {
      var line = parseInt(itemEl.dataset.lineIndex);
      CartAPI.change(line, quantity)
        .then(function (cart) {
          CartDrawer.refreshDrawer();
          if (cart) updateCartBadges(cart.item_count);
        })
        .catch(function (error) {
          Toast.show(error.message || 'Could not update cart', 'error');
          CartDrawer.refreshDrawer();
        });
    },

    refreshDrawer: function () {
      var self = this;
      // Use Section Rendering API to get updated drawer HTML
      // Add timestamp to prevent caching
      var url = window.location.pathname + '?sections=cart-drawer&t=' + Date.now();
      fetch(url)
        .then(safeJson)
        .then(function (data) {
          if (data && data['cart-drawer']) {
            var temp = document.createElement('div');
            temp.innerHTML = data['cart-drawer'];
            var newDrawer = temp.querySelector('[data-cart-drawer]');
            if (newDrawer && self.drawer) {
              // Preserve open state
              var wasOpen = self.drawer.classList.contains('is-open');
              self.drawer.innerHTML = newDrawer.innerHTML;
              if (wasOpen) {
                self.drawer.classList.add('is-open');
              }
              // Re-bind events
              self.bindItemEvents();
              self.bindCloseEvents();
            }
          }
        })
        .catch(function (err) {
          console.error('Failed to refresh cart drawer:', err);
        });
    },

    bindCloseEvents: function () {
      var self = this;
      this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.close(); });
      });
      var overlay = this.drawer.querySelector('[data-cart-drawer-overlay]');
      if (overlay) {
        overlay.addEventListener('click', function () { self.close(); });
      }
    }
  };

  /* ==========================================
     Quick Add to Cart (Product Cards)
     ========================================== */
  function initQuickAdd() {
    // Use event delegation on document so dynamically loaded products (AJAX filtering) also work

    // Standard and hover quick add buttons — use capture to intercept before the parent <a> navigates
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.product-card__quick-add, .product-card__quick-add-hover');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      addToCartFromButton(btn, 1);
    }, true);

    // Bulk quantity decrease
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.product-card__bulk-add [data-action="decrease"]');
      if (!btn) return;
      var input = btn.closest('.product-card__bulk-add').querySelector('.product-card__quantity-input');
      if (input) {
        var val = parseInt(input.value) || 1;
        if (val > 1) input.value = val - 1;
      }
    });

    // Bulk quantity increase
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.product-card__bulk-add [data-action="increase"]');
      if (!btn) return;
      var input = btn.closest('.product-card__bulk-add').querySelector('.product-card__quantity-input');
      if (input) {
        var val = parseInt(input.value) || 1;
        input.value = val + 1;
      }
    });

    // Bulk add to cart
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.product-card__add-btn');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var input = btn.closest('.product-card__bulk-add').querySelector('.product-card__quantity-input');
      var quantity = input ? (parseInt(input.value) || 1) : 1;
      addToCartFromButton(btn, quantity);
    });
  }

  function addToCartFromButton(btn, quantity) {
    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    var originalText = btn.textContent;
    btn.textContent = window.theme.strings.loading || 'Cargando...';
    btn.disabled = true;

    var parsedVariantId = parseInt(variantId);
    var inventoryQuantity = parseInt(btn.dataset.inventoryQuantity) || 999999;
    var inventoryPolicy = btn.dataset.inventoryPolicy || 'deny';

    // If inventory policy is "continue", don't check limits
    if (inventoryPolicy === 'continue') {
      doAddToCart(parsedVariantId, quantity, originalText, btn);
      return;
    }

    // First, check current cart to see if this item exists
    CartAPI.get()
      .then(function (cart) {
        if (!cart || !cart.items) {
          return doAddToCart(parsedVariantId, quantity, originalText, btn);
        }
        var existingItem = cart.items.find(function (item) {
          return item.variant_id === parsedVariantId;
        });
        var currentQtyInCart = existingItem ? existingItem.quantity : 0;
        var totalAfterAdd = currentQtyInCart + quantity;

        if (totalAfterAdd > inventoryQuantity) {
          var canAdd = inventoryQuantity - currentQtyInCart;
          if (canAdd <= 0) {
            // Already at max inventory
            Toast.show(window.theme.strings.inventoryLimit || 'No hay más unidades disponibles', 'error');
            btn.textContent = originalText;
            btn.disabled = false;
            return Promise.reject({ handled: true });
          } else {
            // Add what we can and show warning
            return CartAPI.add(parsedVariantId, canAdd).then(function () {
              Toast.show(
                (window.theme.strings.limitedInventory || 'Debido a la disponibilidad, solo se añadieron') + ' ' + canAdd + ' ' + (window.theme.strings.itemsToCart || 'artículos al carrito'),
                'warning'
              );
              return CartAPI.get();
            });
          }
        } else {
          // Can add full quantity
          return CartAPI.add(parsedVariantId, quantity).then(function () {
            Toast.show(window.theme.strings.itemAdded || 'Articulo agregado al carrito', 'success');
            return CartAPI.get();
          });
        }
      })
      .then(function (cart) {
        if (cart) {
          updateCartBadges(cart.item_count);
          if (window.theme.cartType === 'drawer') {
            CartDrawer.refreshDrawer();
            CartDrawer.open();
          }
        }
      })
      .catch(function (error) {
        if (!error.handled) {
          Toast.show(error.message || 'No se pudo añadir al carrito', 'error');
        }
      })
      .finally(function () {
        btn.textContent = originalText;
        btn.disabled = false;
      });
  }

  function doAddToCart(variantId, quantity, originalText, btn) {
    CartAPI.add(variantId, quantity)
      .then(function () {
        Toast.show(window.theme.strings.itemAdded || 'Articulo agregado al carrito', 'success');
        return CartAPI.get();
      })
      .then(function (cart) {
        if (cart) updateCartBadges(cart.item_count);
        if (window.theme.cartType === 'drawer') {
          CartDrawer.refreshDrawer();
          CartDrawer.open();
        }
      })
      .catch(function (error) {
        Toast.show(error.message || 'No se pudo añadir al carrito', 'error');
      })
      .finally(function () {
        btn.textContent = originalText;
        btn.disabled = false;
      });
  }

  /* ==========================================
     Product Form AJAX Submit
     ========================================== */
  function initProductForms() {
    document.querySelectorAll('[data-product-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var addBtn = form.querySelector('[data-add-to-cart]');
        var textEl = form.querySelector('[data-add-to-cart-text]');
        var spinnerEl = form.querySelector('[data-loading-spinner]');

        if (!addBtn || addBtn.disabled) return;

        var formData = new FormData(form);
        var variantId = formData.get('id');
        var quantity = parseInt(formData.get('quantity')) || 1;

        if (textEl) textEl.hidden = true;
        if (spinnerEl) spinnerEl.hidden = false;
        addBtn.disabled = true;

        CartAPI.add(parseInt(variantId), quantity)
          .then(function () {
            Toast.show(window.theme.strings.itemAdded || 'Item added to cart', 'success');
            return CartAPI.get();
          })
          .then(function (cart) {
            if (cart) updateCartBadges(cart.item_count);
            if (window.theme.cartType === 'drawer') {
              CartDrawer.refreshDrawer();
              CartDrawer.open();
            }
          })
          .catch(function (error) {
            Toast.show(error.message || 'Could not add item to cart', 'error');
          })
          .finally(function () {
            if (textEl) textEl.hidden = false;
            if (spinnerEl) spinnerEl.hidden = true;
            addBtn.disabled = false;
          });
      });
    });
  }

  /* ==========================================
     Predictive Search
     ========================================== */
  function initPredictiveSearch() {
    var input = document.getElementById('search-modal-input');
    var resultsContainer = document.getElementById('predictive-search-results');
    if (!input || !resultsContainer || !window.theme.predictiveSearchEnabled) return;

    var debounceTimer;

    input.addEventListener('input', function () {
      var query = this.value.trim();

      clearTimeout(debounceTimer);

      if (query.length < 2) {
        resultsContainer.innerHTML = '';
        return;
      }

      debounceTimer = setTimeout(function () {
        var url = window.theme.routes.predictiveSearch + '?q=' + encodeURIComponent(query) +
          '&resources[type]=product&resources[limit]=' + (window.theme.searchResultsLimit || 6) +
          '&section_id=predictive-search';

        fetch(url)
          .then(function (res) { return res.text(); })
          .then(function (html) {
            // Try to extract section content
            var tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            var sectionContent = tempDiv.querySelector('#shopify-section-predictive-search');
            if (sectionContent) {
              resultsContainer.innerHTML = sectionContent.innerHTML;
            } else {
              // Fallback: parse JSON for product results
              try {
                var data = JSON.parse(html);
                renderPredictiveResults(data, resultsContainer, query);
              } catch (e) {
                resultsContainer.innerHTML = html;
              }
            }
          })
          .catch(function () {
            resultsContainer.innerHTML = '';
          });
      }, 300);
    });
  }

  function renderPredictiveResults(data, container, query) {
    var products = data.resources && data.resources.results && data.resources.results.products;
    if (!products || products.length === 0) {
      container.innerHTML = '<p style="text-align:center;padding:1rem;opacity:0.5">No results found</p>';
      return;
    }

    var html = '<p class="predictive-search__heading">Products</p><ul class="predictive-search__results">';
    products.forEach(function (product) {
      html += '<li class="predictive-search__item">';
      html += '<a href="' + product.url + '" class="predictive-search__link">';
      if (product.image) {
        html += '<div class="predictive-search__image"><img src="' + product.image + '" alt="" loading="lazy"></div>';
      }
      html += '<div class="predictive-search__info">';
      html += '<p class="predictive-search__title">' + product.title + '</p>';
      html += '<p class="predictive-search__price">' + product.price + '</p>';
      html += '</div>';
      html += '</a></li>';
    });
    html += '</ul>';
    html += '<a href="' + window.theme.routes.search + '?q=' + encodeURIComponent(query) + '" class="predictive-search__view-all">View all results</a>';
    container.innerHTML = html;
  }

  /* ==========================================
     Update Cart Badges
     ========================================== */
  function updateCartBadges(count) {
    // Ensure count is a valid number
    count = typeof count === 'number' ? count : 0;

    document.querySelectorAll('.header__cart-toggle').forEach(function (btn) {
      btn.setAttribute('data-cart-count', count);
      var badge = btn.querySelector('.header__cart-badge');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'header__cart-badge';
          btn.appendChild(badge);
        }
        badge.textContent = count;
        badge.setAttribute('aria-label', count + ' items in cart');
      } else if (badge) {
        badge.remove();
      }
    });

    // Update cart drawer count
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      if (el.classList.contains('cart-drawer__count')) {
        el.textContent = '(' + count + ')';
      }
    });
  }

  /* ==========================================
     Quantity Selectors (Generic)
     ========================================== */
  function initQuantitySelectors() {
    document.querySelectorAll('.cart-table__quantity-selector').forEach(function (selector) {
      var input = selector.querySelector('input');
      var minus = selector.querySelector('[data-minus]');
      var plus = selector.querySelector('[data-plus]');

      if (minus) {
        minus.addEventListener('click', function () {
          var val = parseInt(input.value) || 1;
          if (val > 0) input.value = val - 1;
          input.dispatchEvent(new Event('change'));
        });
      }

      if (plus) {
        plus.addEventListener('click', function () {
          var val = parseInt(input.value) || 0;
          input.value = val + 1;
          input.dispatchEvent(new Event('change'));
        });
      }
    });
  }

  /* ==========================================
     Smooth scroll for anchor links
     ========================================== */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var hash = this.getAttribute('href');
        if (hash === '#') return;
        var target = document.querySelector(hash);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  /* ==========================================
     Collection Sort
     ========================================== */
  function initCollectionSort() {
    document.querySelectorAll('.collection-page__sort-select').forEach(function (select) {
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', this.value);
        window.location.href = url.toString();
      });
    });
  }

  /* ==========================================
     Initialize on DOMContentLoaded
     ========================================== */
  document.addEventListener('DOMContentLoaded', function () {
    CartDrawer.init();
    initQuickAdd();
    initProductForms();
    initPredictiveSearch();
    initQuantitySelectors();
    initSmoothScroll();
    initCollectionSort();
  });

  // Expose for external use
  window.CartAPI = CartAPI;
  window.Toast = Toast;
  window.CartDrawer = CartDrawer;
})();
